import { useMemo } from 'react';
import styles from './IndiaMap.module.css';
import india from '@/assets/geo/india-states.json';
import { osaBand, type MapCity } from '@/hooks/useOsaMap';

/**
 * India choropleth base with one bubble per city — Figma 2881:33285.
 *
 * Rendered as plain SVG from a vendored, simplified GeoJSON rather than by
 * pulling in a charting library: the map is a static outline and ~200 circles,
 * and Highcharts would be ~300 KB to draw it. The boundaries are real WGS84
 * geometry simplified to ~2 km, which is under a pixel at this size.
 *
 * Positions are equirectangular. Over India's ~29 degrees of longitude the
 * distortion against a proper projection is smaller than a bubble radius, and
 * the alternative — shipping a projection library — buys nothing a reader
 * could see.
 */
interface Feature { properties: { name: string }; geometry: { coordinates: number[][][][] } }
const FEATURES = (india as { features: Feature[] }).features;

const VIEW_W = 620;
const VIEW_H = 660;
const PAD = 12;

// India's bounding box in the vendored geometry.
const LON_MIN = 68.0, LON_MAX = 97.5, LAT_MIN = 6.7, LAT_MAX = 35.6;
const SCALE = Math.min(
  (VIEW_W - PAD * 2) / (LON_MAX - LON_MIN),
  (VIEW_H - PAD * 2) / (LAT_MAX - LAT_MIN),
);
const OFF_X = PAD + ((VIEW_W - PAD * 2) - (LON_MAX - LON_MIN) * SCALE) / 2;
const OFF_Y = PAD + ((VIEW_H - PAD * 2) - (LAT_MAX - LAT_MIN) * SCALE) / 2;

const px = (lon: number): number => OFF_X + (lon - LON_MIN) * SCALE;
const py = (lat: number): number => OFF_Y + (LAT_MAX - lat) * SCALE;

export interface IndiaMapProps {
  cities: MapCity[];
  /**
   * Cities drawn, by sales. Plotting all 208 with coordinates turns northern
   * India into one green mass and tells the reader nothing — the design plots
   * roughly this many. The rest stay in the breakdown list beside the map,
   * which is where a long tail is actually readable.
   */
  maxBubbles?: number;
  /** Cities named on the map. The rest are plotted but unlabelled. */
  labelCount?: number;
  selected?: string | undefined;
  onSelect?: (city: string) => void;
  zoom: number;
}

export function IndiaMap({ cities, maxBubbles = 26, labelCount = 12, selected, onSelect, zoom }: IndiaMapProps) {
  const paths = useMemo(
    () =>
      FEATURES.map((f) => ({
        name: f.properties.name,
        d: f.geometry.coordinates
          .map((poly) =>
            poly
              .map((ring) => `M${ring.map(([lon, lat]) => `${px(lon).toFixed(1)},${py(lat).toFixed(1)}`).join('L')}Z`)
              .join(''),
          )
          .join(''),
      })),
    [],
  );

  const plotted = useMemo(
    () =>
      cities
        .filter((c) => c.lat !== null && c.lon !== null)
        .sort((a, b) => b.sales - a.sales)
        .slice(0, maxBubbles),
    [cities, maxBubbles],
  );

  // Bubble area — not radius — tracks sales, so a city twice the size looks
  // twice the size. Scaling radius linearly exaggerates the biggest by its square.
  const maxSales = useMemo(() => Math.max(1, ...plotted.map((c) => c.sales)), [plotted]);
  const radius = (sales: number): number => 9 + Math.sqrt(Math.max(0, sales) / maxSales) * 24;

  /**
   * Labels, biggest first, dropped when they would land on one already placed.
   *
   * Delhi/Gurugram and Mumbai/Pune are close enough to overprint each other at
   * this scale, and two names on top of each other are less use than one — the
   * bubble is still hoverable and the city is still in the list beside the map.
   */
  const labelled = useMemo(() => {
    const placed: Array<{ x: number; y: number }> = [];
    const keep = new Set<string>();
    for (const c of [...plotted].sort((a, b) => b.sales - a.sales)) {
      if (keep.size >= labelCount) break;
      const x = px(c.lon ?? 0);
      const y = py(c.lat ?? 0);
      if (placed.some((p) => Math.abs(p.x - x) < 62 && Math.abs(p.y - y) < 17)) continue;
      placed.push({ x, y });
      keep.add(c.city);
    }
    return keep;
  }, [plotted, labelCount]);

  return (
    <svg
      className={styles.svg}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="img"
      aria-label={`On-shelf availability by city, top ${plotted.length} cities by sales`}
    >
      <g style={{ transform: `scale(${zoom})`, transformOrigin: '50% 50%' }}>
        {paths.map((p) => (
          <path key={p.name} d={p.d} className={styles.state} />
        ))}
        {/* Painted largest-first so a small city is never hidden under a big one. */}
        {[...plotted]
          .sort((a, b) => b.sales - a.sales)
          .map((c) => {
            const r = radius(c.sales);
            const cx = px(c.lon ?? 0);
            const cy = py(c.lat ?? 0);
            const band = osaBand(c.wtOsa);
            const isSel = selected === c.city;
            return (
              <g
                key={c.city}
                className={`${styles.city} ${isSel ? styles.selected : ''}`}
                onClick={() => onSelect?.(c.city)}
              >
                <title>{`${c.city} — Wt OSA ${c.wtOsa ?? '—'}%`}</title>
                <circle cx={cx} cy={cy} r={r} className={`${styles.bubble} ${styles[band]}`} />
                {r >= 13 && c.wtOsa !== null && (
                  <text x={cx} y={cy} className={styles.value} style={{ fontSize: Math.min(13, r * 0.62) }}>
                    {Math.round(c.wtOsa)}
                  </text>
                )}
                {labelled.has(c.city) && (
                  <text x={cx + r + 5} y={cy + 4} className={styles.label}>
                    {c.city.replace(/\b\w/g, (m) => m.toUpperCase())}
                  </text>
                )}
              </g>
            );
          })}
      </g>
    </svg>
  );
}
